import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import React, { FC, useState } from 'react';
import { MyOrderFragment, OrderItem_Booking_Fragment } from '../../../generated/graphql';
import createUnitTitle from '../../../utils/createUnitTitle';
import { applicable, not } from '../../../utils/fp';
import { isDeclined, isDone, isRecent, isReviewable } from '../../../utils/order';
import BookingTile from '../../BookingTile';
import { orderItemIsBooking } from '../../OrderBooking';
import ReviewModal from '../../ReviewModal';
import ReviewTile from '../../ReviewTile';
import { bookingRequiresPayment, isBookingType } from '../../../utils/myOrder';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { FormattedMessage, useIntl } from 'react-intl';
import SectionTitleWrapper from './SectionTitleWrapper';

interface Props {
    orders: MyOrderFragment[];
}

const Actions: FC<React.PropsWithChildren<Props>> = ({ orders }) => {
    const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
    const { formatMessage } = useIntl();
    const requiresPaymentOrders = orders.filter(bookingRequiresPayment);
    const orderItemsWithoutReview = orders.reduce<OrderItem_Booking_Fragment[]>(
        (curr, next) =>
            curr.concat(
                next.orderItems
                    .filter(orderItemIsBooking)
                    .filter(applicable(orderItemIsBooking, isDone, isReviewable, isRecent, not(isDeclined)))
            ),
        []
    );

    const { onClose: closeReviewModal, open: reviewIsOpen, onOpen: openReviewModal } = useSesame();

    const selectedBooking =
        selectedBookingId &&
        orders
            .flatMap(order => order.orderItems)
            .find((item): item is OrderItem_Booking_Fragment => item.id === selectedBookingId);

    return (
        <>
            {(requiresPaymentOrders.length > 0 || orderItemsWithoutReview.length > 0) && (
                <>
                    <SectionTitleWrapper>
                        <Title elementType="h3">
                            <FormattedMessage defaultMessage="Er staan acties voor je klaar" />
                        </Title>
                    </SectionTitleWrapper>
                    {requiresPaymentOrders.map((item, i) => (
                        <BookingTile
                            requiresPayment={bookingRequiresPayment(item)}
                            numBookings={item.orderItems.filter(isBookingType('Booking')).length}
                            numTickets={item.orderItems.filter(isBookingType('Ticket')).length}
                            key={i}
                            orderNumber={item.number!}
                            createdDate={item.createdDate!}
                            orderId={item.id!}
                            orderItems={item.orderItems}
                        />
                    ))}
                    {orderItemsWithoutReview.map((item, i) => (
                        <ReviewTile
                            key={i}
                            title={
                                item.rentalUnit
                                    ? createUnitTitle(
                                          item.rentalUnit?.name!,
                                          item.rentalUnit?.accommodation.name,
                                          item.rentalUnit?.accommodation.hasPublicPage!
                                      )
                                    : formatMessage({
                                          defaultMessage: 'Accommodatie is niet meer beschikbaar',
                                      })
                            }
                            thumbnail={item.rentalUnit?.jpegThumbnail}
                            webThumbnail={item.rentalUnit?.webpThumbnail}
                            arrivalDate={item.arrivalDate}
                            onLeaveReviewClick={() => {
                                setSelectedBookingId(item.id ?? null);
                                openReviewModal();
                            }}
                            isUnavailable={!item?.rentalUnit}
                        />
                    ))}
                    {selectedBooking && (
                        <ReviewModal data={selectedBooking} open={reviewIsOpen} onClose={closeReviewModal} />
                    )}
                </>
            )}
        </>
    );
};

export default Actions;
